import { shouldRemoveHiddenLine, removeLines } from '../components/ReusableFunctions';
import { update_project_file_Api } from '../dashboardapi';

export async function applyAcceptToEditor(editor, item, setTextFile, setAllFiles = null) {
    const session = editor.session;
    const Range = window.ace.require('ace/range').Range;
    if (item.styleInfo?.removeType === 'fullLine') {
        const row = item.startAnchor.row;
        const lineCount = session.getLength();

        if (row < lineCount - 1) {
            // Remove entire line including the trailing \n
            // Range from col 0 of this row to col 0 of next row = entire line + \n
            const range = new Range(row, 0, row + 1, 0);
            session.remove(range);
        } else {
            // Last line — just clear content, no \n to remove
            const range = new Range(row, 0, row, session.getLine(row).length);
            session.remove(range);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    if (item.styleInfo?.removeType === 'partial') {
        // Remove only the % comment part — generic fallback handles this fine
        // since suggestionText is '' and the range covers only the comment
        const range = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.remove(range);
        if (setTextFile) setTextFile(session.getValue());
        return;
    }
    if (item.styleInfo?.isBibitemKeyChange) {
        const { oldKey, newKey, allfiles, currentFileUuid } = item.styleInfo;

        // ── Step 1: Replace \bibitem{oldKey} in current editor ───────────────
        const bibRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.replace(bibRange, item.suggestionText);

        // ── Step 2: Also update \cite{oldKey} in the SAME file ───────────────
        // After accepting \bibitem, the \cite anchor for this key is now stale
        // (it still references the old key). Fix it directly in the session.
        const escapedOld = oldKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const citeRegex = new RegExp(
            `(~?\\\\cite\\{[^}]*?)\\b${escapedOld}\\b([^}]*?\\})`,
            'g'
        );

        const currentContent = session.getValue();
        const citeMatches = [];
        let citeMatch;

        // Collect all \cite matches in current file containing oldKey
        const tempRegex = new RegExp(citeRegex.source, 'g');
        while ((citeMatch = tempRegex.exec(currentContent)) !== null) {
            citeMatches.push({
                index: citeMatch.index,
                length: citeMatch[0].length,
                replacement: citeMatch[0].replace(
                    new RegExp(`\\b${escapedOld}\\b`, 'g'),
                    newKey
                )
            });
        }

        // Apply bottom-to-top so indices don't shift
        for (let i = citeMatches.length - 1; i >= 0; i--) {
            const cm = citeMatches[i];
            const startPos = session.doc.indexToPosition(cm.index);
            const endPos = session.doc.indexToPosition(cm.index + cm.length);
            const cRange = new Range(startPos.row, startPos.column, endPos.row, endPos.column);
            session.replace(cRange, cm.replacement);
        }

        if (setTextFile) setTextFile(session.getValue());

        // ── Step 3: Cross-file \cite update ──────────────────────────────────
        if (!Array.isArray(allfiles)) {
            console.warn('[CitationStyling] allfiles is not an array:', typeof allfiles);
            return;
        }

        const updatedFiles = [];

        for (const fileObj of allfiles) {
            if (!fileObj.content) continue;
            if (!fileObj.name?.endsWith('.tex')) continue;
            if (fileObj.file_uuid === currentFileUuid) continue;

            // Normalise line endings only for comparison
            const normalised = fileObj.content.replace(/\r\n/g, '\n');
            citeRegex.lastIndex = 0;
            const updatedNormalised = normalised.replace(
                citeRegex,
                (_, before, after) => `${before}${newKey}${after}`
            );

            if (updatedNormalised === normalised) {
                console.log(`[CitationStyling] no \\cite{${oldKey}} in ${fileObj.name} — skipped`);
                continue;
            }

            // Preserve original \r\n for the saved content
            citeRegex.lastIndex = 0;
            const updatedContent = fileObj.content.replace(
                citeRegex,
                (_, before, after) => `${before}${newKey}${after}`
            );

            try {
                console.log(`[CitationStyling] saving ${fileObj.name}...`);
                await update_project_file_Api(
                    fileObj.project_uuid,
                    fileObj.file_uuid,
                    fileObj.name,
                    updatedContent
                );
                console.log(`✅ Saved ${fileObj.name}: \\cite{${oldKey}} → \\cite{${newKey}}`);
                updatedFiles.push({ ...fileObj, content: updatedContent });
            } catch (err) {
                console.error(`❌ Failed to save ${fileObj.name}:`, err);
            }
        }

        // ── Step 4: Refresh Allfiles state ────────────────────────────────────
        if (updatedFiles.length > 0 && typeof setAllFiles === 'function') {
            setAllFiles(prev => {
                // Handle both shapes: plain array OR { data: [], error: null }
                const prevData = Array.isArray(prev) ? prev : (prev?.data || []);
                const newData = prevData.map(f => {
                    const updated = updatedFiles.find(u => u.file_uuid === f.file_uuid);
                    return updated ?? f;
                });
                return Array.isArray(prev) ? newData : { ...prev, data: newData };
            });
            console.log(`[CitationStyling] Allfiles state refreshed for ${updatedFiles.length} file(s)`);
        } else if (updatedFiles.length > 0 && !setAllFiles) {
            console.warn('[CitationStyling] setAllFiles not provided — in-memory Allfiles is stale');
        }

        return;
    }


    if (item.styleInfo?.isPreambleInsertion) {
        const { textAboveTitle } = item.styleInfo;
        const currentContent = session.getValue();

        // Find \title again in current content
        const titleMatch = /\\title\s*\{/i.exec(currentContent);
        if (!titleMatch) return;

        const titlePos = session.doc.indexToPosition(titleMatch.index);

        // Replace everything from (0,0) to \title position with preamble
        const range = new Range(0, 0, titlePos.row, titlePos.column);
        session.replace(range, item.suggestionText);

        if (setTextFile) setTextFile(session.getValue());
        return;
    }
    // if (item.styleInfo?.isListLevelInsertion) {
    //     const currentContent = session.getValue();
    //     const docMatch = /\\begin\{document\}/i.exec(currentContent);
    //     if (!docMatch) return;

    //     const docPos = session.doc.indexToPosition(docMatch.index);
    //     // Replace at the position of \begin{document}
    //     const range = new Range(docPos.row, docPos.column, docPos.row, docPos.column);
    //     session.insert(range, item.suggestionText);

    //     if (setTextFile) setTextFile(session.getValue());
    //     return;
    // }
    if (item.styleInfo?.isListLevelInsertion) {
        const currentContent = session.getValue();
        // 1. Find the current index of \begin{document}
        const docMatch = /\\begin\{document\}/i.exec(currentContent);
        if (!docMatch) return;

        // 2. Convert that index to a Row/Column position
        const docPos = session.doc.indexToPosition(docMatch.index);

        // 3. Define the insertion point (Row of the match, Column 0) 
        // to ensure it goes on the line ABOVE the command.
        const insertPos = { row: docPos.row, column: 0 };

        // 4. Insert the suggestion text + a newline to push the command down
        session.insert(insertPos, item.suggestionText);

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    if (item.styleInfo?.authorAction === 'moveAfterShorttitle') {
        const shorttitleText = item.styleInfo.shorttitleText;
        const fixedAuthor = item.suggestionText;
        const contentBefore = session.getValue();
        const shorttitleIndex = contentBefore.indexOf(shorttitleText);
        const shorttitleEnd = shorttitleIndex + shorttitleText.length;
        const deleteRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        const textBetween = contentBefore.slice(shorttitleEnd, contentBefore.indexOf(item.originalText));
        const isAlreadyAfter = /^\s*$/.test(textBetween);

        if (isAlreadyAfter) {
            session.replace(deleteRange, fixedAuthor);
        } else {
            session.remove(deleteRange);
            const contentAfterDelete = session.getValue();
            const newShorttitleIndex = contentAfterDelete.indexOf(shorttitleText);
            if (newShorttitleIndex === -1) {
                session.insert({ row: 0, column: 0 }, fixedAuthor + '\n\n');
            } else {
                const insertAfterIndex = newShorttitleIndex + shorttitleText.length;
                const insertPos = session.doc.indexToPosition(insertAfterIndex);
                session.insert(insertPos, `\n\n${fixedAuthor}`);
            }
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    if (item.styleInfo?.name === "Proper Label and Ref") {
        const oldLabel = item.styleInfo.oldLabelName;
        const newLabel = item.styleInfo.newLabelName;
        const labelRange = new Range(
            item.startAnchor.row, item.startAnchor.column,
            item.endAnchor.row, item.endAnchor.column
        );
        session.replace(labelRange, item.suggestionText);

        if (oldLabel && newLabel) {
            const escapedOld = oldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const refPattern = new RegExp(
                `\\\\(?:ref|eqref|autoref|cref|Cref|pageref)\\s*\\{${escapedOld}\\}`,
                'g'
            );
            const currentContent = session.getValue();
            let match;
            const refMatches = [];
            while ((match = refPattern.exec(currentContent)) !== null) {
                refMatches.push({
                    index: match.index,
                    length: match[0].length,
                    fullMatch: match[0],
                    replacement: match[0].replace(oldLabel, newLabel)
                });
            }
            for (let i = refMatches.length - 1; i >= 0; i--) {
                const ref = refMatches[i];
                const startPos = session.doc.indexToPosition(ref.index);
                const endPos = session.doc.indexToPosition(ref.index + ref.length);
                const refRange = new Range(startPos.row, startPos.column, endPos.row, endPos.column);
                session.replace(refRange, ref.replacement);
            }
            console.log(`Renamed \\label{${oldLabel}} → \\label{${newLabel}}, updated ${refMatches.length} refs`);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }

    // Inside applyAcceptToEditor...

    if (item.styleInfo?.maketitleAction === 'moveAndClean') {
        // --- STEP 1: REMOVE ALL EXISTING \maketitle ---
        let currentContent = session.getValue();
        const maketitleRegex = /\\maketitle/g;
        let match;
        const matchesToRemove = [];

        // Find all occurrences first to avoid index shifting issues during loop
        while ((match = maketitleRegex.exec(currentContent)) !== null) {
            matchesToRemove.push({
                index: match.index,
                length: match[0].length
            });
        }

        // Remove from bottom to top to preserve indices
        for (let i = matchesToRemove.length - 1; i >= 0; i--) {
            const m = matchesToRemove[i];
            const startPos = session.doc.indexToPosition(m.index);
            const endPos = session.doc.indexToPosition(m.index + m.length);
            const rangeToRemove = new Range(startPos.row, startPos.column, endPos.row, endPos.column);
            session.remove(rangeToRemove);
        }

        // --- STEP 2: INSERT THE NEW ONE AT \begin{document} ---
        // We refresh the content because the row/column of the anchor might have shifted 
        // if the deleted \maketitle was above the \begin{document}
        const refreshedContent = session.getValue();
        const docMatch = /\\begin\{document\}/.exec(refreshedContent);

        if (docMatch) {
            const startPos = session.doc.indexToPosition(docMatch.index);
            const endPos = session.doc.indexToPosition(docMatch.index + docMatch[0].length);
            const finalDocRange = new Range(startPos.row, startPos.column, endPos.row, endPos.column);

            session.replace(finalDocRange, item.suggestionText);
        } else {
            // Fallback: Use the original anchor if the text search fails
            const fallbackRange = new Range(
                item.startAnchor.row, item.startAnchor.column,
                item.endAnchor.row, item.endAnchor.column
            );
            session.replace(fallbackRange, item.suggestionText);
        }

        if (setTextFile) setTextFile(session.getValue());
        return;
    }


    // ── Generic fallback replace ──────────────────────────────────────────────
    const range = new Range(
        item.startAnchor.row, item.startAnchor.column,
        item.endAnchor.row, item.endAnchor.column
    );
    session.replace(range, item.suggestionText);
    if (setTextFile) setTextFile(session.getValue());
}

export function cleanupItem(session, item) {
    if (item.markerId) session.removeMarker(item.markerId);
    if (item.startAnchor?.detach) item.startAnchor.detach();
    if (item.endAnchor?.detach) item.endAnchor.detach();
}

export function removeFromAnchorList(globalAnchorList, item) {
    const index = globalAnchorList.indexOf(item);
    if (index > -1) globalAnchorList.splice(index, 1);
}